// ============ Human Design — the per-activation reading + today's sky ============
//
// Two things the chart could not say before:
//
//   ผังวันนี้ — Human Design is not only a birth chart. The sky keeps moving,
//               and where it lights the missing half of one of your hanging
//               gates, that channel is complete for as long as the transit
//               lasts. That is the difference between two identical setups on
//               two different days, which is exactly the sort of thing a
//               trading desk should be told.
//   อ่านรายดาว — the activation table gives 26 coordinates. Each of them is a
//               planet (what it governs), a gate (what it is), a line (how it
//               operates), and a colour/tone (which the Variables read). Here
//               every row opens into all of that.

import React, { useMemo, useState } from 'react';
import { useT, useLang } from '../lib/i18n';
import { computeHD, GATE_TH, GATE_EN, CENTER_GATES } from '../lib/humandesign';
import {
  quarterOfGate, lineRead, PLANET_BY_KEY, readTransit, LINES,
} from '../lib/hdDetail';
import { CENTRES, GATE_CIRCUIT, CIRCUIT_BY_ID } from '../lib/hdStructure';
import { SecHead } from './core';

const CENTRE_TH: Record<string, string> = Object.fromEntries(CENTRES.map(c => [c.id, c.th]));
const CENTRE_EN: Record<string, string> = Object.fromEntries(CENTRES.map(c => [c.id, c.en]));

const DESIGN_C = '#ff5c4d';
const PERSONALITY_C = '#e8ecf5';

export function HdDetail({ hd }: { hd: any }) {
  const t = useT();
  const { lang } = useLang();
  const en = lang === 'en';
  const [side, setSide] = useState<'personality' | 'design'>('personality');
  const [open, setOpen] = useState<string | null>(null);

  const gateName = (g: number) => (en ? GATE_EN[g] : GATE_TH[g]) || `Gate ${g}`;
  const centreName = (c: string) => (en ? CENTRE_EN[c] : CENTRE_TH[c]) || c;

  // The sky only needs recomputing when the day turns, so the key is the date
  // rather than the clock — otherwise every render re-runs the ephemeris.
  const dayKey = new Date().toISOString().slice(0, 10);
  const sky = useMemo(() => computeHD(new Date()).personality, [dayKey]);
  const transit = useMemo(
    () => readTransit(sky.map((a: any) => a.gate), hd.activeGates, hd.definedCenters),
    [sky, hd.activeGates, hd.definedCenters]);

  const list = side === 'personality' ? hd.personality : hd.design;
  const persGates = useMemo(() => new Set(hd.personality.map((a: any) => a.gate)), [hd.personality]);
  const desGates = useMemo(() => new Set(hd.design.map((a: any) => a.gate)), [hd.design]);
  const activeSet = useMemo(() => new Set(hd.activeGates), [hd.activeGates]);
  const definedChannelGates = useMemo(() => {
    const m = new Map<number, number>();
    for (const [a, b] of hd.definedChannels) { m.set(a, b); m.set(b, a); }
    return m;
  }, [hd.definedChannels]);

  const skySun = sky.find((a: any) => a.k === 'sun');
  const skyEarth = sky.find((a: any) => a.k === 'earth');

  return (
    <React.Fragment>
      {/* ------------------------------------------------ today's sky ---- */}
      <div className="mm-card glass">
        <SecHead th="ผังวันนี้ · ท้องฟ้ากำลังทำอะไรกับดวงคุณ" en="TODAY'S TRANSIT · what the sky is doing to your chart"
          right={<span className="mono dim">{new Date().toLocaleDateString(en ? 'en-GB' : 'th-TH', { day: 'numeric', month: 'short', year: 'numeric' })}</span>}></SecHead>

        <div className="mm-hdt-sun">
          {skySun && (
            <div className="mm-hdt-sunbox">
              <span className="mono dim">{t('ดวงอาทิตย์วันนี้', "Today's Sun")}</span>
              <b>{skySun.gate}.{skySun.line}</b>
              <span>{gateName(skySun.gate)}</span>
              <em className="dim">{en ? lineRead(skySun.line).en : lineRead(skySun.line).th}</em>
            </div>
          )}
          {skyEarth && (
            <div className="mm-hdt-sunbox">
              <span className="mono dim">{t('โลกวันนี้', "Today's Earth")}</span>
              <b>{skyEarth.gate}.{skyEarth.line}</b>
              <span>{gateName(skyEarth.gate)}</span>
              <em className="dim">{en ? lineRead(skyEarth.line).en : lineRead(skyEarth.line).th}</em>
            </div>
          )}
        </div>

        {transit.channels.length > 0 ? (
          <React.Fragment>
            <b className="mm-hdt-h mono dim">{t('ช่องที่ท้องฟ้าต่อให้ครบชั่วคราว', 'Channels the sky completes for you today')}</b>
            <div className="mm-hdt-chans">
              {transit.channels.map(c => (
                <div className="mm-hdt-chan" key={c.gates.join('-')}>
                  <b className="mono">{c.gates[0]}-{c.gates[1]}</b>
                  <span><i className="mine">{c.mine}</i> {gateName(c.mine)} <em className="dim">{t('ของคุณ', 'yours')}</em></span>
                  <span><i className="sky">{c.sky}</i> {gateName(c.sky)} <em className="dim">{t('จากท้องฟ้า', 'from the sky')}</em></span>
                  <small className="dim">{centreName(c.centres[0])} ↔ {centreName(c.centres[1])}</small>
                </div>
              ))}
            </div>
            {transit.newCentres.length > 0 && (
              <p className="mm-hdt-new">
                <b>{t('ศูนย์ที่ถูกกำหนดชั่วคราววันนี้', 'Centres temporarily defined today')}:</b>{' '}
                {transit.newCentres.map(c => centreName(c)).join(' · ')}
              </p>
            )}
            <p className="mm-footnote dim">{t(
              'ประตูค้างของคุณคือครึ่งช่อง — วันที่ท้องฟ้าถือครึ่งที่เหลือ ช่องนั้นจะครบชั่วคราว และคุณจะรู้สึกว่ามีความสามารถบางอย่างที่ปกติไม่มี นั่นคือเหตุผลที่ setup เดียวกันคนละวันให้ความรู้สึกไม่เหมือนกัน · ระวังการเข้าใจผิดว่านี่คือตัวจริงของคุณ',
              'Your hanging gates are half-channels. On a day the sky holds the other half, that channel completes and a capacity you do not normally have shows up. It is why the same setup feels different on two days — and why it is easy to mistake a transit for your own nature.')}</p>
          </React.Fragment>
        ) : (
          <p className="dim">{t('วันนี้ท้องฟ้าไม่ได้ต่อช่องไหนให้คุณเพิ่ม — ดวงเดินตามปกติของตัวเอง', 'The sky completes no channel for you today — the chart runs as itself.')}</p>
        )}

        {transit.echoed.length > 0 && (
          <p className="mm-footnote dim">
            {t('ประตูที่ท้องฟ้าย้ำซ้ำกับของคุณวันนี้', 'Gates the sky doubles up on today')}:{' '}
            {transit.echoed.map(g => `${g} ${gateName(g)}`).join(' · ')}
          </p>
        )}
      </div>

      {/* --------------------------------------------- per-activation ---- */}
      <div className="mm-card glass">
        <SecHead th="อ่านรายดาว · ทั้ง 26 การกระตุ้น" en="ACTIVATION BY ACTIVATION · all 26"
          right={<span className="mono dim">13 + 13</span>}></SecHead>

        <div className="mm-filterrow" style={{ marginBottom: 10 }}>
          {([['personality', t('Personality · จิตสำนึก', 'Personality · conscious'), PERSONALITY_C],
             ['design', t('Design · ร่างกาย', 'Design · body'), DESIGN_C]] as const).map(([s, label, c]) => (
            <button key={s} className={`mm-pill ${side === s ? 'on' : ''}`} onClick={() => setSide(s as any)}
              style={side === s ? { borderColor: c } : undefined}>{label}</button>
          ))}
        </div>

        <div className="mm-hdt-acts">
          {list.map((a: any) => {
            const pl = PLANET_BY_KEY.get(a.k);
            const ln = lineRead(a.line);
            const q = quarterOfGate(a.gate);
            const centre = CENTRES.find(c => (CENTER_GATES[c.id] || []).includes(a.gate));
            const partner = definedChannelGates.get(a.gate);
            const isOpen = open === `${side}-${a.k}`;
            const both = persGates.has(a.gate) && desGates.has(a.gate);
            return (
              <div className={`mm-hdt-act ${isOpen ? 'open' : ''}`} key={a.k}
                onClick={() => setOpen(isOpen ? null : `${side}-${a.k}`)}>
                <div className="mm-hdt-arow">
                  <span className="mm-hdt-sym">{a.sym}</span>
                  <span className="mm-hdt-pname">{en ? a.en : a.th}</span>
                  <b className="mono" style={{ color: side === 'design' ? DESIGN_C : PERSONALITY_C }}>{a.gate}.{a.line}</b>
                  <span className="mm-hdt-gname">{gateName(a.gate)}</span>
                  <em className="mono dim">{a.color}/{a.tone}</em>
                </div>
                {isOpen && (
                  <div className="mm-hdt-adet">
                    {pl && <p><b className="mono dim">{t('ดาวนี้ทำอะไร', 'What this planet governs')}</b> {en ? pl.readEn : pl.readTh}
                      {pl.weightTh && <em className="dim"> · {en ? '~70% of the imprint' : pl.weightTh}</em>}</p>}
                    <p><b className="mono dim">{t('เส้นที่', 'Line')} {a.line} · {en ? ln.en : ln.th}</b> {en ? ln.readEn : ln.readTh}</p>
                    <p className="mm-hdt-market">{en ? ln.marketEn : ln.marketTh}</p>
                    <div className="mm-hdt-tags">
                      {centre && <span className="mm-ich-chip">{t('ศูนย์', 'centre')} {centreName(centre.id)}</span>}
                      <span className="mm-ich-chip">{en ? CIRCUIT_BY_ID.get(GATE_CIRCUIT[a.gate])!.en : CIRCUIT_BY_ID.get(GATE_CIRCUIT[a.gate])!.th}</span>
                      <span className="mm-ich-chip" style={{ color: q.colour, borderColor: q.colour + '55' }}>{en ? q.en : q.th}</span>
                      {partner
                        ? <span className="mm-ich-chip good">{t('ช่องครบ', 'channel complete')} {Math.min(a.gate, partner)}-{Math.max(a.gate, partner)}</span>
                        : activeSet.has(a.gate) && <span className="mm-ich-chip warn">{t('ประตูค้าง — รออีกครึ่ง', 'hanging — waiting for its other half')}</span>}
                      {both && <span className="mm-ich-chip gold">{t('มีทั้งสองฝั่ง — ตอกย้ำสองเท่า', 'on both sides — doubled')}</span>}
                    </div>
                    <p className="mm-footnote dim">{t(
                      `สี ${a.color} / โทน ${a.tone} / เบส ${a.base} — ชั้นย่อยของวงล้อที่ Variables (PHS) อ่าน ไม่ใช่ตัวเลขประดับ`,
                      `Colour ${a.color} / tone ${a.tone} / base ${a.base} — the sub-divisions of the wheel the Variables read, not decoration.`)}</p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
        <p className="mm-footnote dim">{t(
          'Personality คือสิ่งที่คุณรู้ตัวว่าเป็น · Design คือสิ่งที่ร่างกายทำอยู่แล้วโดยไม่ต้องสั่ง และมักเป็นสิ่งที่คนอื่นเห็นในตัวคุณก่อนคุณเอง · กดแต่ละแถวเพื่ออ่านทั้งดาว เส้น วงจร และจตุภาค',
          'Personality is what you are aware of. Design is what the body already does unasked — usually what others see in you before you do. Tap a row for the planet, the line, the circuit and the quarter.')}</p>
      </div>
    </React.Fragment>
  );
}
